import { useState, useEffect, forwardRef, useRef } from "react";
import { styled, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import { keyframes } from "@mui/system";

// Styled components
const Container = styled(Box)({
  position: "relative",
  width: "100%",
  maxWidth: "380px",
});

const HiddenInput = styled("input")({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: "100%",
  height: "100%",
  opacity: 0,
  zIndex: 10,
  cursor: "text",
});

const DigitsContainer = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
  border: "0px solid #dcdddc",
});

const pulse = keyframes`
  0% {
    opacity: 0.2;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.2;
  }
`;

const DigitBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isActive" && prop !== "isLoading",
})<{ isActive: boolean; isLoading: boolean }>(
  ({ theme, isActive, isLoading }) => ({
    width: "50px",
    height: "61px",
    backgroundColor: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "32px",
    fontWeight: 600,
    border: "1px solid #dcdddc",
    borderRadius: "8px",
    animation: isLoading ? `${pulse} 1.5s ease-in-out infinite` : "none",
    [theme.breakpoints.down("md")]: {
      width: "40px",
      height: "50px",
      fontSize: "24px",
    },
  })
);

const blinkingCursorPulse = keyframes`
  0% {
    opacity: 0;
  }
  49% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 1;
  }
`;

const BlinkingCursor = styled("span")(({ theme }) => ({
  display: "inline-block",
  width: "2px",
  height: "1.5em",
  background: theme.palette.primary.main,
  marginLeft: "2px",
  animation: `${blinkingCursorPulse} 1s steps(2, start) infinite`,
  verticalAlign: "middle",
}));

const OTPVerificationInput = forwardRef(
  (
    {
      value = "",
      onChange,
      maxLength = 6,
      error = false,
      disabled = false,
    }: {
      value: string;
      onChange: (value: string) => void;
      maxLength: number;
      error: boolean;
      disabled: boolean;
    },
    ref: React.Ref<HTMLInputElement>
  ) => {
    const [inputValue, setInputValue] = useState(value);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isFocused, setIsFocused] = useState(false);
    const theme = useTheme();
    const inputRef = useRef<HTMLInputElement>(null);
    // Sync with external value prop
    useEffect(() => {
      setInputValue(value);
    }, [value]);

    // Keep activeIndex in sync with value length
    useEffect(() => {
      if (inputValue.length < maxLength) {
        setActiveIndex(inputValue.length);
      } else {
        setActiveIndex(maxLength - 1);
      }
    }, [inputValue, maxLength]);

    // Blur and hide cursor if input is disabled (e.g., on error/verification)
    useEffect(() => {
      if (disabled && isFocused) {
        setIsFocused(false);
        if (inputRef.current) {
          inputRef.current.blur();
        }
      }
    }, [disabled, isFocused]);

    // Handle input change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (!/^\d*$/.test(newValue)) return;
      const limitedValue = newValue.slice(0, maxLength);
      setInputValue(limitedValue);
      if (onChange) {
        onChange(limitedValue);
      }
      // Move activeIndex to next empty slot or last
      if (limitedValue.length < maxLength) {
        setActiveIndex(limitedValue.length);
      } else {
        setActiveIndex(maxLength - 1);
      }
    };

    // Handle paste event
    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedData = e.clipboardData.getData("text");
      if (!/^\d+$/.test(pastedData)) {
        return;
      }
      const limitedValue = pastedData.slice(0, maxLength);
      setInputValue(limitedValue);
      if (onChange) {
        onChange(limitedValue);
      }
      e.preventDefault();
      setActiveIndex(Math.min(limitedValue.length, maxLength - 1));
    };

    // Focus hidden input when clicking a box
    const handleBoxClick = (index: number) => {
      setActiveIndex(index);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    };

    // Create array of single characters for display
    const valueArray = inputValue.split("");
    const emptySlots = Math.max(0, maxLength - valueArray.length);
    const displayArray = [...valueArray, ...Array(emptySlots).fill("")];

    return (
      <Container>
        {/* Hidden actual input field */}
        <HiddenInput
          ref={(node) => {
            // Only assign to our own ref if it's a mutable ref object
            if (
              node &&
              inputRef &&
              Object.prototype.hasOwnProperty.call(inputRef, "current")
            ) {
              (
                inputRef as React.MutableRefObject<HTMLInputElement | null>
              ).current = node;
            }
            if (typeof ref === "function") ref(node);
          }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={inputValue}
          onChange={handleChange}
          onPaste={handlePaste}
          onKeyDown={(e) => {
            if (disabled) return;
            if (e.key === "Backspace") {
              if (
                inputValue.length > 0 &&
                activeIndex > 0 &&
                inputValue[activeIndex] === undefined
              ) {
                setActiveIndex((prev) => Math.max(0, prev - 1));
              }
            } else if (/^\d$/.test(e.key)) {
              // Move to next box on digit entry
              if (activeIndex < maxLength - 1) {
                setTimeout(
                  () =>
                    setActiveIndex((prev) => Math.min(maxLength - 1, prev + 1)),
                  0
                );
              }
            }
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          maxLength={maxLength}
          disabled={disabled}
        />
        {/* Visual representation of input as separate boxes */}
        <DigitsContainer>
          {displayArray.map((digit, index) => (
            <DigitBox
              key={index}
              isActive={index === activeIndex && !disabled}
              isLoading={disabled}
              sx={{
                borderColor: error ? theme.palette.error.main : "inherit",
                cursor: disabled ? "not-allowed" : "pointer",
              }}
              onClick={() => !disabled && handleBoxClick(index)}
            >
              {digit}
              {index === activeIndex && !disabled && isFocused && (
                <BlinkingCursor />
              )}
            </DigitBox>
          ))}
        </DigitsContainer>
      </Container>
    );
  }
);

export default OTPVerificationInput;
